/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { roundToDecimalPlaces } from "./mathTools.js";
/**
 * Telemetry class that accumulates measurements which are eventually logged in a telemetry event through the provided
 * {@link TelemetryEventBatcher.logger | logger} when the number of calls to the function reaches the specified {@link TelemetryEventBatcher.threshold | threshold}.
 *
 * @remarks It is expected to be used for a single event type. If different properties should be logged at different times, a separate `TelemetryEventBatcher` should be created with separate `TMetrics` type.
 * @typeparam TMetrics - The set of keys that should be logged.
 * E.g., `keyof Foo` for logging properties `bar` and `baz` from `type Foo = { bar: number, baz: number }`.
 *
 * @sealed
 * @internal
 */
export class TelemetryEventBatcher {
    constructor(
    /**
     * Custom properties to include in the telemetry performance event when it is written.
     */
    eventBase, 
    /**
     * The logger to use to write the telemetry performance event.
     */
    logger, 
    /**
     * The number of logs to accumulate before sending the data to the logger.
     */
    threshold) {
        this.eventBase = eventBase;
        this.logger = logger;
        this.threshold = threshold;
        /**
         * Stores the sum of the custom data passed into the logger.
         */
        this.dataSums = {};
        /**
         * Stores the maximum value of the custom data passed into the logger.
         */
        this.dataMaxes = {};
        /**
         * Counter to keep track of the number of times the log function is called.
         */
        this.counter = 0;
    }
    /**
     * Accumulates the custom data and sends it to the logger every {@link TelemetryEventBatcher.threshold} calls.
     *
     * @param customData -
     * A record storing the custom data to be accumulated and eventually logged.
     */
    accumulateAndLog(customData) {
        for (const key of Object.keys(customData)) {
            this.dataSums[key] = (this.dataSums[key] ?? 0) + customData[key];
            this.dataMaxes[key] = Math.max(this.dataMaxes[key] ?? Number.NEGATIVE_INFINITY, customData[key]);
        }
        this.counter++;
        if (this.counter >= this.threshold) {
            this.sendData();
        }
    }
    sendData() {
        const telemetryEvent = {
            ...this.eventBase,
        };
        for (const key of Object.keys(this.dataSums)) {
            if (this.dataSums[key] !== undefined) {
                telemetryEvent[`avg_${key}`] = roundToDecimalPlaces(this.dataSums[key] / this.counter, 6);
            }
            if (this.dataMaxes[key] !== undefined) {
                telemetryEvent[`max_${key}`] = this.dataMaxes[key];
            }
        }
        this.logger.sendPerformanceEvent(telemetryEvent);
        // Reset the counter and the data.
        this.counter = 0;
        this.dataSums = {};
        this.dataMaxes = {};
    }
}
//# sourceMappingURL=telemetryEventBatcher.js.map